<script setup lang="ts">
import { computed } from 'vue';
import { BCard, BChartSkeleton } from '@/design-system';

type PeriodOptionType = {
    key: string;
    label: string;
};

type PropsType = {
    title: string;
    periods?: PeriodOptionType[];
    activePeriod?: string;
    isLoading?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    periods: () => [
        { key: 'week', label: 'Týden' },
        { key: 'month', label: 'Měsíc' },
        { key: 'year', label: 'Rok' },
    ],
    activePeriod: 'month',
    isLoading: false,
});

const emit = defineEmits<{
    'update:active-period': [period: string];
}>();

const currentPeriod = computed(() => props.activePeriod);

const selectPeriod = (key: string) => {
    emit('update:active-period', key);
};

defineOptions({ inheritAttrs: false });
</script>

<template>
    <BCard
        class="b-dashboard-chart-card"
        variant="flat"
        v-bind="$attrs"
        title-class="pt-0 px-0"
        text-class="px-0"
    >
        <template #title>
            <div class="b-dashboard-chart-card__header">
                <h3 class="b-dashboard-chart-card__title">
                    {{ title }}
                </h3>

                <div class="b-dashboard-chart-card__header-actions">
                    <div class="b-dashboard-chart-card__period-tabs">
                        <button
                            v-for="period in periods"
                            :key="period.key"
                            type="button"
                            class="b-dashboard-chart-card__period-tab"
                            :class="{
                                'b-dashboard-chart-card__period-tab--active': currentPeriod === period.key,
                            }"
                            @click="selectPeriod(period.key)"
                        >
                            {{ period.label }}
                        </button>
                    </div>
                </div>
            </div>
        </template>

        <template #default>
            <div class="b-dashboard-chart-card__content">
                <BChartSkeleton v-if="isLoading" />
                <slot v-else />
            </div>

            <div
                v-if="$slots.footer"
                class="b-dashboard-chart-card__footer"
            >
                <slot name="footer" />
            </div>
        </template>
    </BCard>
</template>

<style scoped lang="scss">
@use '@/design-system/styles/dashboard-animations.scss';

.b-dashboard-chart-card {
    height: 100%;
    border-radius: 8px;
    border: 1px solid rgb(204, 225, 235);
    padding: 20px 30px;
    transition: box-shadow 0.2s ease;
}

.b-dashboard-chart-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.b-dashboard-chart-card__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    width: 100%;
}

.b-dashboard-chart-card__title {
    font-size: 14px;
    font-weight: 600;
    color: rgb(var(--v-theme-on-surface));
    margin: 0;
}

.b-dashboard-chart-card__header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
}

.b-dashboard-chart-card__period-tabs {
    display: flex;
    gap: 3px;
    padding: 3px;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 8px;
}

.b-dashboard-chart-card__period-tab {
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 500;
    color: rgba(var(--v-theme-on-surface), 0.6);
    background: transparent;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(&--active) {
        color: rgba(var(--v-theme-on-surface), 0.8);
        background: rgba(var(--v-theme-on-surface), 0.03);
    }

    &--active {
        color: rgb(var(--v-theme-primary));
        background: rgb(var(--v-theme-surface));
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
}

.b-dashboard-chart-card__footer {
    padding-top: 16px;
}

@media (max-width: 768px) {
    .b-dashboard-chart-card__header {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .b-dashboard-chart-card__header-actions {
        justify-content: space-between;
    }

    .b-dashboard-chart-card__period-tabs {
        flex: 1;
    }

    .b-dashboard-chart-card__period-tab {
        flex: 1;
        text-align: center;
    }
}
</style>
